import React, { useContext, useEffect, useMemo } from "react";
import { FieldPath, FieldValues } from "react-hook-form";
import { GroupBase } from "react-select";
import {
  ReactSelectSyncProps,
  ReactSelectAsyncProps,
  ReactSelectController,
  ReactSelectControllerProps,
} from "@pushpress/shared-components";
import { AppContext } from "../../../context/app";
import {
  useGetSubscriptionsQuery,
  SubscriptionOptionFragment,
} from "./SusbcriptionSelect-generated.hooks";
import {
  filterSubscriptions,
  sortSubscriptionsByDate,
} from "./SubscriptionSelect.utils";

type Props = {
  userUuid: string;
  onLoadOptions?: (
    subscriptions: SubscriptionOptionFragment[] | undefined,
  ) => void;
  calendarItemUuid?: string;
  forceEnabled?: boolean;
  noPortal?: boolean;
  menuPortalTarget?: HTMLElement;
};

type SubscriptionControllerProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Option extends SubscriptionOptionFragment,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = ReactSelectControllerProps<TFieldValues, TName, Option, IsMulti, Group>;

function SubscriptionController<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Option extends SubscriptionOptionFragment,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: SubscriptionControllerProps<
    TFieldValues,
    TName,
    Option,
    IsMulti,
    Group
  > &
    (ReactSelectSyncProps | ReactSelectAsyncProps) &
    Props,
) {
  const {
    userUuid,
    calendarItemUuid,
    onLoadOptions,
    forceEnabled,
    noPortal,
    menuPortalTarget,
    ...other
  } = props;
  const app = useContext(AppContext);
  const { data } = useGetSubscriptionsQuery({
    variables: {
      getSubscriptionInput: {
        clientUuid: app.client.uuid,
        userUuid,
        calendarItemUuid: calendarItemUuid || null,
        limit: null,
        page: null,
      },
    },
  });

  const getOptionLabel = (option: SubscriptionOptionFragment) =>
    option.planObject.name;
  const getOptionValue = (option: SubscriptionOptionFragment) =>
    String(option.subscriptionUuid);

  const options = useMemo(() => {
    if (!data?.getSubscriptions?.subscriptions) {
      return undefined;
    }
    const filteredSubscriptions = filterSubscriptions(
      data?.getSubscriptions.subscriptions,
    );
    const sortedSubscriptions = [...filteredSubscriptions].sort(
      sortSubscriptionsByDate,
    );

    return sortedSubscriptions;
  }, [data]) as SubscriptionOptionFragment[] | undefined;

  useEffect(() => {
    onLoadOptions?.(options);
  }, [options]);

  return (
    <ReactSelectController
      {...(!noPortal && {
        menuPortalTarget: menuPortalTarget || document.body,
      })}
      {...other}
      options={options || ([] as any)}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      disabled={!forceEnabled && options?.length === 1}
    />
  );
}

export default SubscriptionController;
